import { timelineExpandedAtom } from '../../atoms/workspace';
import { trpc } from '../../lib/trpc';
import { shouldQueryWorkspaceActivity } from './activityQuery';
import { TimelineEntry } from './TimelineEntry';
import { Center, Drawer, Loader, SegmentedControl } from '@mantine/core';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';

type TimelineDrawerProps = {
  readonly workspaceId: null | string;
};

type Timescale = 'Day' | 'Month' | 'Week';

const getTimeRange = (timescale: Timescale): { from: Date; to: Date } => {
  const now = new Date();
  switch (timescale) {
    case 'Day':
      return { from: dayjs().startOf('day').toDate(), to: now };
    case 'Month':
      return { from: dayjs().startOf('month').toDate(), to: now };
    case 'Week':
      return { from: dayjs().startOf('week').toDate(), to: now };
    default:
      return { from: now, to: now };
  }
};

export const TimelineDrawer = ({ workspaceId }: TimelineDrawerProps) => {
  const [isExpanded, setIsExpanded] = useAtom(timelineExpandedAtom);
  const [timescale, setTimescale] = useState<Timescale>('Day');

  const { from, to } = useMemo(() => getTimeRange(timescale), [timescale]);

  const { data: activityResult, isLoading: activityLoading } =
    trpc.activity.list.useQuery(
      {
        from,
        to,
        workspaceId: workspaceId as string,
      },
      { enabled: isExpanded && shouldQueryWorkspaceActivity(workspaceId) },
    );

  const entries = activityResult?.ok ? activityResult.entries : [];

  return (
    <Drawer
      onClose={() => setIsExpanded(false)}
      opened={isExpanded}
      position="right"
      size="90%"
      title="Timeline"
      withCloseButton
    >
      {/* Timescale tabs */}
      <SegmentedControl
        className="mb-3"
        data={['Day', 'Week', 'Month']}
        onChange={(value) => setTimescale(value as Timescale)}
        size="xs"
        value={timescale}
      />

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {activityLoading ? (
          <Center className="pt-16">
            <Loader size="sm" />
          </Center>
        ) : entries.length === 0 ? (
          <Center className="pt-16">
            <span className="text-sm text-black/30 dark:text-white/30">
              No activity in this period
            </span>
          </Center>
        ) : (
          <div className="py-2">
            {entries.map((entry) => (
              <TimelineEntry
                createdAt={new Date(entry.createdAt)}
                itemContent={entry.itemContent}
                key={entry.id}
                processTitle={entry.processTitle}
                type={entry.type}
                workspaceTitle={entry.workspaceTitle}
              />
            ))}
          </div>
        )}
      </div>
    </Drawer>
  );
};
